<template>
  <div class="notification-toast-container">
    <transition-group name="notification-fade" tag="div">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="notification-toast"
        :class="notification.type"
      >
        <!-- Header栏 -->
        <div class="notification-header">
          <span class="notification-title">{{ notification.title || getDefaultTitle(notification.type) }}</span>
          <button class="notification-close" @click.stop="removeNotification(notification.id)">
            <el-icon><Close /></el-icon>
          </button>
        </div>
        
        <!-- 消息内容 -->
        <div 
          class="notification-message" 
          v-if="!notification.htmlMessage"
          :style="{ cursor: notification.onClick ? 'pointer' : 'default' }"
        >
          {{ notification.message }}
          <span 
            v-if="notification.showActionButton !== false && notification.onClick" 
            class="notification-action-button"
            @click.stop="handleNotificationClick(notification)"
          >
            立即处理
          </span>
        </div>
        <div 
          class="notification-message" 
          v-else 
          :style="{ cursor: notification.onClick ? 'pointer' : 'default' }"
        >
          <span v-html="notification.htmlMessage"></span>
          <span 
            v-if="notification.showActionButton !== false && notification.onClick" 
            class="notification-action-button"
            @click.stop="handleNotificationClick(notification)"
          >
            立即处理
          </span>
        </div>
       
        <!-- 进度条 -->
        <div v-if="notification.showProgress" class="notification-progress">
          <div 
            class="notification-progress-bar" 
            :style="{ width: `${notification.progress}%` }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from "vue";
import { Close } from "@element-plus/icons-vue";
import { useRouter } from "vue-router";

const router = useRouter();

// 定义通知选项接口
interface NotificationOptions {
  id: string;
  message: string;
  title?: string;
  htmlMessage?: string;
  type?: 'success' | 'warning' | 'error' | 'info';
  duration?: number;
  showProgress?: boolean;
  progress?: number;
  onClick?: (() => void) | string;
  linkText?: string;
  linkRoute?: string;
  autoCloseOnClick?: boolean;
  showActionButton?: boolean; // 控制是否显示"立即处理"按钮
}

// 通知列表
const notifications = ref<NotificationOptions[]>([]);
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const progressTimers = new Map<string, ReturnType<typeof setInterval>>();

// 获取默认标题
const getDefaultTitle = (type?: string): string => {
  const titles: Record<string, string> = {
    success: '成功',
    warning: '警告',
    error: '错误',
    info: '提示'
  };
  return titles[type || 'info'] || '提示';
};

// 处理通知点击事件
const handleNotificationClick = (notification: NotificationOptions) => {
  if (notification.onClick) {
    if (typeof notification.onClick === 'function') {
      notification.onClick();
    } else if (typeof notification.onClick === 'string') {
      router.push(notification.onClick);
    }
  }
  if (notification.autoCloseOnClick !== false) {
    removeNotification(notification.id);
  }
};

const clickFn = ()=>{
    const notificationLinks: any = document.querySelectorAll('.notification-toast a[data-route]');
    for (const link of notificationLinks) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const route = (link as HTMLElement).dataset.route;
        if (route) {
          router.push(route);
        }
        
        // 找到对应的通知并关闭
        const linkText = link.textContent?.trim();
        if (linkText) {
          // 查找匹配的通知
          const notification = notifications.value.find(n => 
            n.linkRoute === route && n.linkText === linkText
          );
          // 如果找到匹配的通知，则关闭它
          if (notification && notification.autoCloseOnClick !== false) {
            removeNotification(notification.id);
          }
        }
      });
    }
}

// 处理富文本中的链接点击
const setupLinkHandlers = () => {
    setTimeout(() => {
      clickFn()
    })
};


// 显示通知
const showNotification = (options: Omit<NotificationOptions, 'id' | 'progress'>) => {
  const id = Date.now().toString();
  
  let htmlMessage = options.htmlMessage;
  if (!htmlMessage && options.linkText && options.linkRoute) {
    htmlMessage = `${options.message} <a href="#" data-route="${options.linkRoute}" style="color: #409eff; text-decoration: none;">${options.linkText}</a>`;
  }
  
  const notification: NotificationOptions = {
    id,
    message: options.message,
    title: options.title,
    htmlMessage,
    type: options.type || 'info',
    duration: options.duration || 3000,
    showProgress: options.showProgress !== false,
    progress: 100,
    onClick: options.onClick,
    linkText: options.linkText,
    linkRoute: options.linkRoute,
    autoCloseOnClick: options.autoCloseOnClick,
    showActionButton: options.showActionButton !== false // 默认显示按钮
  };
  
  notifications.value.push(notification);
  
  if (htmlMessage) {
    setupLinkHandlers();
  }
  
  if (notification.duration > 0) {
    if (notification.showProgress) {
      const intervalTime = 30;
      const totalSteps = notification.duration / intervalTime;
      const progressDecrement = 100 / totalSteps;
      
      const progressInterval = setInterval(() => {
        const index = notifications.value.findIndex(n => n.id === id);
        if (index !== -1) {
          notifications.value[index].progress = Math.max(0, notifications.value[index].progress! - progressDecrement);
        }
      }, intervalTime);
      progressTimers.set(id, progressInterval);
    }
    
    const timer = globalThis.setTimeout(() => {
      removeNotification(id);
    }, notification.duration);
    timers.set(id, timer);
  }
  
  return id;
};

// 移除通知
const removeNotification = (id: string) => {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  
  const progressTimer = progressTimers.get(id);
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimers.delete(id);
  }
  
  const index = notifications.value.findIndex(n => n.id === id);
  if (index !== -1) {
    notifications.value.splice(index, 1);
  }
};

// 清除所有通知
const clearAll = () => {
  for (const [, timer] of timers) {
    clearTimeout(timer);
  }
  for (const [, timer] of progressTimers) {
    clearInterval(timer);
  }
  timers.clear();
  progressTimers.clear();
  
  notifications.value = [];
};

// 组件卸载时清理定时器
onUnmounted(() => {
  clearAll();
});

// 暴露方法供父组件使用
defineExpose({
  showNotification,
  removeNotification,
  clearAll
});
</script>

<style scoped>
.notification-toast-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}

.notification-toast {
  max-width: 320px;
  min-width: 300px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
}

.notification-toast:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  color: white;
  font-weight: 500;
  font-size: 14px;
}

.notification-toast.success .notification-header {
  background-color: #67c23a;
}

.notification-toast.warning .notification-header {
  background-color: #e6a23c;
}

.notification-toast.error .notification-header {
  background-color: #f56c6c;
}

.notification-toast.info .notification-header {
  background-color: #409eff;
}

.notification-title {
  flex: 1;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.notification-toast.success .notification-close:hover,
.notification-toast.warning .notification-close:hover,
.notification-toast.error .notification-close:hover,
.notification-toast.info .notification-close:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.notification-message {
  word-break: break-word;
  line-height: 2;
  font-size: 14px;
  padding: 24px 20px;
}

.notification-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(0, 0, 0, 0.05);
}

.notification-progress-bar {
  height: 100%;
  background: #409eff;
  transition: width 0.1s linear;
}

.notification-toast.success .notification-progress-bar {
  background: #67c23a;
}

.notification-toast.warning .notification-progress-bar {
  background: #e6a23c;
}

.notification-toast.error .notification-progress-bar {
  background: #f56c6c;
}

.notification-toast.info .notification-progress-bar {
  background: #409eff;
}

.notification-fade-enter-active,
.notification-fade-leave-active {
  transition: all 0.3s ease;
}

.notification-fade-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-fade-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
.notification-action-button {
  color: #409eff;
  cursor: pointer;
  margin-left: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
  display: inline-block;
}

.notification-action-button:hover {
  color: #66b1ff;
  text-decoration: underline;
}

</style>